import { IAppContext } from 'App/AppContext';
import { TFunction } from 'i18next';
import {
  DocumentProviderCapabilities,
  DocumentProviderType,
  getProviderName,
  isBrokeredProvider,
} from 'models/documentProvider';
import { apiGetResourceListName } from 'services/Api/listService';
import { apiRequest } from 'services/Auth/authConfig';
import AppError from 'utils/appError';

export enum ResourceListType {
  DocumentLibrary = 0,
  CustomList = 1,
  WebURL = 2,
  SitePageLibrary = 3,
}

/**
 * The values 1, 2, 5 and 6 were the output purposes' categories; those are bound in Admin, Settings, Reports now and
 * read from globalDataCache.outputLocations. The lists endpoint still projects them for the Outlook add-ins.
 */
export enum ResourceListSystemType {
  Assets = 3, // To link a list of sub(assets) to an asset like computers, mobile devices etc.
  Threats = 4, // List to link to a risk to select threats
}

export default class ResourceList {
  tenantId?: string;

  listId: number;

  authSchemaId?: number;

  listType: ResourceListType;

  systemListId?: ResourceListSystemType;

  /**
   * Which system holds this category's content. For a non-SharePoint provider the sp* fields
   * below carry that provider's opaque ids instead: spDriveId is the container and
   * spDriveItemId the root folder inside it.
   */
  providerType: DocumentProviderType;

  /** The tenant connection this category reaches its provider through; unset for SharePoint, virtual and Loop. */
  connectionId?: number;

  spSiteId?: string;

  spListId?: string;

  spDriveId?: string;

  spDriveItemId?: string;

  name: string;

  webURL?: string;

  altTenantId?: string;

  altTenantName?: string;

  columnProps?: string;

  enableMetaData?: boolean;

  isVirtual?: boolean;

  skipPreview?: boolean;

  constructor() {
    this.listId = -1;
    this.listType = ResourceListType.DocumentLibrary;
    this.providerType = DocumentProviderType.SharePoint;
    this.spSiteId = '';
    this.spListId = '';
    this.spDriveId = '';
    this.spDriveItemId = '';
    this.name = '';
    this.webURL = '';
  }

  /**
   * Versioning and everything that rests on it (publication, approvals, read confirmations).
   * Pass the provider capabilities to gate on them; without them the answer is the SharePoint
   * one, which is what every existing caller expects.
   */
  canEnableMetaData = (capabilities?: DocumentProviderCapabilities): boolean => {
    if (capabilities && !capabilities.versions) return false;

    return this.listType === ResourceListType.DocumentLibrary || this.listType === ResourceListType.SitePageLibrary;
  };

  isEqual(item: ResourceList): boolean {
    if (item.listId !== this.listId) return false;
    if (item.listType !== this.listType) return false;
    if (item.providerType !== this.providerType) return false;
    if (item.connectionId !== this.connectionId) return false;
    if (item.spSiteId !== this.spSiteId) return false;
    if (item.spDriveId !== this.spDriveId) return false;
    if (item.spListId !== this.spListId) return false;
    if (item.spDriveItemId !== this.spDriveItemId) return false;
    if (item.name !== this.name) return false;
    if (item.webURL !== this.webURL) return false;
    if (item.altTenantId !== this.altTenantId) return false;
    if (item.altTenantName !== this.altTenantName) return false;
    if (item.columnProps !== this.columnProps) return false;
    if (item.enableMetaData !== this.enableMetaData) return false;
    if (item.isVirtual !== this.isVirtual) return false;
    if (item.authSchemaId !== this.authSchemaId) return false;
    if (item.skipPreview !== this.skipPreview) return false;

    return true;
  }

  clone = (): ResourceList => {
    const output = new ResourceList();

    output.listId = this.listId;
    output.listType = this.listType;
    output.providerType = this.providerType;
    output.connectionId = this.connectionId;
    output.spSiteId = this.spSiteId;
    output.spDriveId = this.spDriveId;
    output.spListId = this.spListId;
    output.spDriveItemId = this.spDriveItemId;
    output.name = this.name;
    output.webURL = this.webURL;
    output.altTenantId = this.altTenantId;
    output.altTenantName = this.altTenantName;
    output.columnProps = this.columnProps;
    output.enableMetaData = this.enableMetaData;
    output.isVirtual = this.isVirtual;
    output.authSchemaId = this.authSchemaId;
    output.skipPreview = this.skipPreview;

    return output;
  };

  static async getUniqueName(orgName: string, appContext: IAppContext): Promise<string> {
    let suffix: number = 1;

    const accessToken = await appContext.getAccessToken(apiRequest.scopes);
    let listExists: number = await apiGetResourceListName(orgName, accessToken);

    let newName: string = orgName;

    while (listExists) {
      const name = newName + ' ' + suffix.toString();
      listExists = await apiGetResourceListName(name, accessToken);
      if (suffix > 100) throw new AppError('Create new list name failed: 100 iterations reached');
      suffix++;
      newName = name;
    }

    return newName;
  }

  static async nameExists(orgName: string, appContext: IAppContext): Promise<number> {
    const accessToken = await appContext.getAccessToken(apiRequest.scopes);

    return await apiGetResourceListName(orgName, accessToken);
  }

  getListTypeText(t: TFunction, type?: ResourceListType): string {
    // A category on a document provider is named after its provider. The kind underneath is
    // the SharePoint shape it borrows so the library keeps working - "Document library" says
    // nothing true about a Google Drive or Notion category. An explicit type argument still
    // wins: that caller is asking what a kind is called, not what this category is.
    if (type === undefined && isBrokeredProvider(this)) {
      return getProviderName(this.providerType);
    }

    switch (type ?? this.listType) {
      case ResourceListType.DocumentLibrary:
        return t('lists:List.Type.DocumentLibrary');
      case ResourceListType.CustomList:
        return t('lists:List.Type.CustomList');
      case ResourceListType.WebURL:
        return t('lists:List.Type.URLList');
      case ResourceListType.SitePageLibrary:
        return t('lists:List.Type.SitePageLibrary');
      default:
        return '';
    }
  }
}
